import "./characteristics.page.css";

import { useTranslation } from "react-i18next";

import { resolveAbilityId, systemLabelKey } from "../../../catalogs/characterSystemCatalog";
import { useD20Roll } from "../../../hooks/useD20Roll";
import CharacterAvatar from "../CharacterAvatar";
import HitPoints from "../HitPoints/HitPoints";
import RollResultModal from "../RollResultModal";
import Characteristic from "./Characteristic";
import SavingThrow from "./SavingThrow";
import StatBlock from "./StatBlock";

const abilityModifier = (characteristics, abilityId) => {
  const ability = characteristics.mainCharacteristicList.find(
    (item) => resolveAbilityId(item.abilityId || item.name) === resolveAbilityId(abilityId)
  );
  return Math.floor(((Number(ability?.diceValue) || 10) - 10) / 2);
};

const racialAdjustment = (ability) => Number(ability.racialAdjustment ?? ability.ruleAdjustment) || 0;
const readNumericInput = (event, fallback = 0) => {
  const nextValue = event.target.valueAsNumber;
  return Number.isFinite(nextValue) ? nextValue : fallback;
};

const Characteristics = ({ character, onChange, token }) => {
  const { t } = useTranslation();
  const characteristics = character.characteristics;
  const { result, roll, closeRoll } = useD20Roll();
  const update = (changes) =>
    onChange({ ...character, characteristics: { ...characteristics, ...changes } });

  const saveModifierBreakdown = (save) => {
    const ability = abilityModifier(characteristics, save.abilityId);
    const remainder = Number(save.modifier) - ability;
    return [
      { label: t(systemLabelKey("abilities", save.abilityId)), value: ability },
      ...(remainder !== 0 ? [{ label: t("roll.other"), value: remainder }] : []),
    ];
  };

  return (
    <div className="quarter">
      <div className="characteristics-container">
        <CharacterAvatar character={character} onChange={onChange} token={token} />
        <div className="characteristics">
          <div className="upper-characteristics">
            {characteristics.mainCharacteristicList.map((item, index) => {
              const label = t(systemLabelKey("abilities", item.abilityId));
              return (
                <Characteristic
                  characteristic={item}
                  inputId={`ability-${item.abilityId}`}
                  inputValue={item.diceValue}
                  key={item.abilityId}
                  label={label}
                  onChange={(diceValue) =>
                    update({
                      mainCharacteristicList: characteristics.mainCharacteristicList.map(
                        (current, currentIndex) =>
                          currentIndex === index ? {
                            ...current,
                            baseDiceValue: diceValue
                              - Number(current.manualAdjustment || 0)
                              - racialAdjustment(current),
                          } : current
                      ),
                    })
                  }
                  onRoll={(_, modifier) =>
                    roll({
                      label: t("roll.abilityCheck", { ability: label }),
                      modifier,
                      modifierBreakdown: [{ label, value: modifier }],
                    })
                  }
                />
              );
            })}
          </div>
          <RollResultModal result={result} onClose={closeRoll} />
          <div className="lower-characteristics">
            <HitPoints
              hitDice={character.ruleDerived?.hitDice}
              hp={characteristics.hp}
              onChange={(hp) => update({ hp })}
            />
            <StatBlock label={t("character.armorClass")} readOnly value={characteristics.armorClass} />
            <StatBlock
              label={t("character.baseAttack")}
              value={characteristics.baseAttackModifier}
              onChange={(event) => update({
                manualBaseAttackModifier: readNumericInput(event) - Number(characteristics.classBaseAttackBonus || 0),
              })}
            />
            <StatBlock
              label={t("character.initiative")}
              value={characteristics.initiative}
              onChange={(event) => update({ initiative: readNumericInput(event) })}
              onRoll={() => roll({
                label: t("roll.initiative"),
                modifier: Number(characteristics.initiative) || 0,
                modifierBreakdown: [{
                  label: t("character.initiative"),
                  value: Number(characteristics.initiative) || 0,
                }],
              })}
              rollLabel={t("roll.initiativeAction")}
            />
            <StatBlock
              label={t("character.walking")}
              value={characteristics.walkingDistance}
              onChange={(event) => update({ walkingDistance: readNumericInput(event) })}
            />
            <div className="saving-throws">
              <strong>{t("character.savingThrows")}</strong>
              {characteristics.pathfinderSaves.map((item, index) => {
                const label = t(systemLabelKey("saves", item.saveId));
                return (
                  <SavingThrow
                    key={item.saveId}
                    label={label}
                    value={item.modifier}
                    onChange={(modifier) =>
                      update({
                        pathfinderSaves: characteristics.pathfinderSaves.map(
                          (current, currentIndex) =>
                            currentIndex === index ? {
                              ...current,
                              manualModifier: modifier
                                - abilityModifier(characteristics, current.abilityId)
                                - Number(current.classBaseBonus || 0)
                                - Number(current.racialBonus || 0),
                            } : current
                        ),
                      })
                    }
                    onRoll={() =>
                      roll({
                        label: t("roll.savingThrow", { save: label }),
                        modifier: item.modifier,
                        modifierBreakdown: saveModifierBreakdown(item),
                      })
                    }
                  />
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Characteristics;
